import { createSelector } from 'reselect';

import { StoreState } from '../../../reducers';
import { IndesignTextFieldsConfig } from '../utils/indesignEditor/editableTextFields/getData';
import { HtmlTextFieldsConfig } from '../utils/htmlEditor/editableTextFields/getData';
import { IndesignMediaElementConfig } from '../utils/indesignEditor/editableMedia/getData';
import { HtmlMediaElementConfig } from '../utils/htmlEditor/editableMedia/getData';
import { LinkConfig } from '../utils/editableLinks/getData';
import { ButtonConfig } from '../utils/indesignEditor/editableButtons/getData';
import { IframeConfig } from '../utils/indesignEditor/editableIframes/getData';
import { EnrichmentConfig } from '../utils/indesignEditor/editableEnrichments/getData';
import { SliderConfig } from '../utils/indesignEditor/editableSliders/getData';
import { ThemedItemConfig } from '../utils/indesignEditor/themedItems/getData';
import {
	EditableTextFieldsState,
	EditableMediaState,
	EditableLinksState,
	EditableButtonsState,
	EditableIframesState,
	EditableEnrichmentsState,
	EditableSlidersState,
	ThemedItemsState,
	DuplicatedItemsInSpreadState,
} from '../reducer';

export type EditableItem =
	IndesignTextFieldsConfig |
	HtmlTextFieldsConfig |
	IndesignMediaElementConfig |
	HtmlMediaElementConfig |
	LinkConfig |
	ButtonConfig |
	IframeConfig |
	EnrichmentConfig |
	SliderConfig |
	ThemedItemConfig;

type EditableItems =
	EditableTextFieldsState |
	EditableMediaState |
	EditableLinksState |
	EditableButtonsState |
	EditableIframesState |
	EditableEnrichmentsState |
	EditableSlidersState |
	ThemedItemsState;

const getCurrentSpread = (state: StoreState): number[] => state.editor.pagesController.spreads[state.editor.pagesController.currentSpreadIndex];

interface SpreadItems {
	// for saving: a shared item carries the data of the page it was drawn on
	all: EditableItem[];
	// for rendering: only the items this page owns get an editor
	own: EditableItem[];
}

// inDesign copies an item that crosses the fold into both page files, with the same class in each. The class
// also names the page the item was drawn on, counted inside the inDesign spread, which is not always the pair
// of pages shown together - so an item is shared only when the adjacent page really has it.
const getSpreadItems = (
	items: EditableItems,
	page: number,
	duplicatedItemsInSpread?: DuplicatedItemsInSpreadState,
	spread?: number[],
): SpreadItems => {
	const pageItems: EditableItem[] = items[page] ? Object.values(items[page]) : [];
	const markedItems = duplicatedItemsInSpread && duplicatedItemsInSpread[page];
	// this spread holds the pages on screen, so any other page has no neighbour in it
	const adjacentPage = spread && spread.indexOf(page) !== -1
		? spread.find(pageInSpread => pageInSpread !== page)
		: undefined;

	if (!markedItems || !adjacentPage || !items[adjacentPage]) {
		return { all: pageItems, own: pageItems };
	}

	const adjacentPageItems: EditableItem[] = Object.values(items[adjacentPage]);
	const own: EditableItem[] = [];
	const all = pageItems.map((item) => {
		const marked = markedItems[item.id];
		// all kinds of item share one list of marks, keyed by element id, and two kinds can land on one element
		const isOwnMark = !!marked && 'className' in item && item.className === marked.className;
		const adjacentPageItem = isOwnMark && adjacentPageItems.find(adjacentItem => (
			'className' in adjacentItem && !!adjacentItem.className && adjacentItem.className.includes(marked.className)
		));

		if (!adjacentPageItem) {
			own.push(item);

			return item;
		}

		return { ...adjacentPageItem, ...marked };
	});

	return { all, own };
};

const getPageEditableItems = (
	items: EditableItems,
	page: number,
	duplicatedItemsInSpread?: DuplicatedItemsInSpreadState,
	spread?: number[],
): EditableItem[] => getSpreadItems(items, page, duplicatedItemsInSpread, spread).all;

const getPageOwnEditableItemsIds = (
	items: EditableItems,
	page: number,
	duplicatedItemsInSpread?: DuplicatedItemsInSpreadState,
	spread?: number[],
): string[] => getSpreadItems(items, page, duplicatedItemsInSpread, spread).own.map(item => item.id);

const getEditableTextFields = (state: StoreState): EditableTextFieldsState => state.editor.editableTextFields;
const getEditableMedia = (state: StoreState): EditableMediaState => state.editor.editableMedia;
const getEditableLinks = (state: StoreState): EditableLinksState => state.editor.editableLinks;
const getEditableButtons = (state: StoreState): EditableButtonsState => state.editor.editableButtons;
const getEditableIframes = (state: StoreState): EditableIframesState => state.editor.editableIframes;
const getEditableEnrichments = (state: StoreState): EditableEnrichmentsState => state.editor.editableEnrichments;
const getEditableSliders = (state: StoreState): EditableSlidersState => state.editor.editableSliders;
const getThemedItems = (state: StoreState): ThemedItemsState => state.editor.themedItems;
const getDuplicatedItemsInSpread = (state: StoreState): DuplicatedItemsInSpreadState => state.editor.duplicatedItemsInSpread;

export const getPageEditableTextFields = createSelector(
	getEditableTextFields,
	getDuplicatedItemsInSpread,
	getCurrentSpread,
	(state: StoreState, page: number): number => page,
	(items: EditableTextFieldsState, duplicatedItemsInSpread: DuplicatedItemsInSpreadState, spread: number[], page: number): EditableItem[] => (
		getPageEditableItems(items, page, duplicatedItemsInSpread, spread)
	),
);

export const getPageEditableMedia = createSelector(
	getEditableMedia,
	getDuplicatedItemsInSpread,
	getCurrentSpread,
	(state: StoreState, page: number): number => page,
	(items: EditableMediaState, duplicatedItemsInSpread: DuplicatedItemsInSpreadState, spread: number[], page: number): EditableItem[] => (
		getPageEditableItems(items, page, duplicatedItemsInSpread, spread)
	),
);

export const getPageEditableLinks = createSelector(
	getEditableLinks,
	(state: StoreState, page: number): number => page,
	(items: EditableLinksState, page: number): EditableItem[] => getPageEditableItems(items, page),
);

export const getPageEditableButtons = createSelector(
	getEditableButtons,
	getDuplicatedItemsInSpread,
	getCurrentSpread,
	(state: StoreState, page: number): number => page,
	(items: EditableButtonsState, duplicatedItemsInSpread: DuplicatedItemsInSpreadState, spread: number[], page: number): EditableItem[] => (
		getPageEditableItems(items, page, duplicatedItemsInSpread, spread)
	),
);

export const getPageEditableIframes = createSelector(
	getEditableIframes,
	(state: StoreState, page: number): number => page,
	(items: EditableIframesState, page: number): EditableItem[] => getPageEditableItems(items, page),
);

export const getPageEditableEnrichments = createSelector(
	getEditableEnrichments,
	(state: StoreState, page: number): number => page,
	(items: EditableEnrichmentsState, page: number): EditableItem[] => getPageEditableItems(items, page),
);

export const getPageEditableSliders = createSelector(
	getEditableSliders,
	getDuplicatedItemsInSpread,
	getCurrentSpread,
	(state: StoreState, page: number): number => page,
	(items: EditableSlidersState, duplicatedItemsInSpread: DuplicatedItemsInSpreadState, spread: number[], page: number): EditableItem[] => (
		getPageEditableItems(items, page, duplicatedItemsInSpread, spread)
	),
);

export const getPageEditableTextFieldsIds = createSelector(
	getEditableTextFields,
	getDuplicatedItemsInSpread,
	getCurrentSpread,
	(state: StoreState, page: number): number => page,
	(items: EditableTextFieldsState, duplicatedItemsInSpread: DuplicatedItemsInSpreadState, spread: number[], page: number): string[] => (
		getPageOwnEditableItemsIds(items, page, duplicatedItemsInSpread, spread)
	),
);

export const getPageEditableMediaIds = createSelector(
	getEditableMedia,
	getDuplicatedItemsInSpread,
	getCurrentSpread,
	(state: StoreState, page: number): number => page,
	(items: EditableMediaState, duplicatedItemsInSpread: DuplicatedItemsInSpreadState, spread: number[], page: number): string[] => (
		getPageOwnEditableItemsIds(items, page, duplicatedItemsInSpread, spread)
	),
);

export const getPageEditableLinksIds = createSelector(
	getEditableLinks,
	(state: StoreState, page: number): number => page,
	(items: EditableLinksState, page: number): string[] => getPageEditableItems(items, page).map(item => item.id),
);

export const getPageEditableButtonsIds = createSelector(
	getEditableButtons,
	getDuplicatedItemsInSpread,
	getCurrentSpread,
	(state: StoreState, page: number): number => page,
	(items: EditableButtonsState, duplicatedItemsInSpread: DuplicatedItemsInSpreadState, spread: number[], page: number): string[] => (
		getPageOwnEditableItemsIds(items, page, duplicatedItemsInSpread, spread)
	),
);

export const getPageEditableIframesIds = createSelector(
	getEditableIframes,
	(state: StoreState, page: number): number => page,
	(items: EditableIframesState, page: number): string[] => getPageEditableItems(items, page).map(item => item.id),
);

export const getPageEditableEnrichmentsIds = createSelector(
	getEditableEnrichments,
	(state: StoreState, page: number): number => page,
	(items: EditableEnrichmentsState, page: number): string[] => getPageEditableItems(items, page).map(item => item.id),
);

export const getPageEditableSlidersIds = createSelector(
	getEditableSliders,
	getDuplicatedItemsInSpread,
	getCurrentSpread,
	(state: StoreState, page: number): number => page,
	(items: EditableSlidersState, duplicatedItemsInSpread: DuplicatedItemsInSpreadState, spread: number[], page: number): string[] => (
		getPageOwnEditableItemsIds(items, page, duplicatedItemsInSpread, spread)
	),
);

export const getPageThemedItemsIds = createSelector(
	getThemedItems,
	(state: StoreState, page: number): number => page,
	(items: ThemedItemsState, page: number): string[] => getPageEditableItems(items, page).map(item => item.id),
);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/selectors/editableItems.ts